import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Autocomplete,
  DialogContentText,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Menu as MenuIcon,
  AccountCircle,
  Refresh as RefreshIcon,
  List as ListIcon,
  Create as CreateIcon,
  FolderOpen as FolderOpenIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import TeamComposition from '../components/TeamComposition';
import DraftBoard from '../components/DraftBoard';
import AuthDialog from '../components/AuthDialog';
import MatchDataPull from '../components/MatchDataPull';
import { useAuth } from '../contexts/AuthContext';
import { useDraft } from '../contexts/DraftContext';
import { Team } from '../models/DraftState';
import { HeroData } from '../models/HeroData';

const PageContainer = styled(Container)`
  padding: 24px;
  max-width: 1200px;
`;

const Header = styled(Paper)`
  padding: 16px;
  background-color: #2a2a2a;
  margin-bottom: 24px;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled(Typography)`
  color: white;
  margin-right: 16px;
`;

const InfoFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    background-color: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const StyledAppBar = styled(AppBar)`
  background-color: #1a1a1a;
`;

const ControlsContainer = styled(Paper)`
  padding: 16px;
  margin-bottom: 24px;
  background-color: #1e1e1e;
`;

const ControlsHeader = styled(Typography)`
  margin-bottom: 16px;
  color: #fff;
`;

const HeroSelector = styled(Autocomplete)`
  margin-bottom: 16px;
`;

/**
 * DraftSimulator Page Component
 */
const DraftSimulator = () => {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const { currentUser, signOut } = useAuth();
  const {
    saveDraft,
    teamName,
    setTeamName,
    tournament,
    setTournament,
    draftDate,
    setDraftDate,
    loadDraft,
    currentDraftId,
    hasUnsavedChanges,
    scenariosFirstPick,
    updateScenario
  } = useDraft();

  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [draftInfo, setDraftInfo] = useState({
    title: '',
    tournament: '',
    date: '',
    isPublic: false,
  });
  const [draftsMenuAnchor, setDraftsMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [saveDraftDialogOpen, setSaveDraftDialogOpen] = useState(false);
  const [saveMode, setSaveMode] = useState('save'); // 'save' or 'saveAs'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Track the currently selected box
  const [selectedBox, setSelectedBox] = useState(-1);
  
  // Show notification function for displaying alerts
  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);
  
  // Get all available heroes for the autocomplete
  const allHeroes = Object.values(HeroData).map(hero => ({
    id: hero.id,
    name: hero.name,
    imageUrl: hero.imageUrl
  }));
  
  // Current selected hero
  const currentScenario = scenariosFirstPick.length > 0 ? scenariosFirstPick[0] : null;
  const currentHero = selectedBox !== -1 && currentScenario?.picks?.[selectedBox - 1]
    ? currentScenario.picks[selectedBox - 1].hero 
    : null;

  // Save the current draft
  const handleSaveClick = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get radiant and dire heroes from current scenario picks
      const currentPicks = currentScenario?.picks || Array(24).fill(null);
      const radiantHeroes = currentPicks.slice(0, 12).filter(pick => pick !== null);
      const direHeroes = currentPicks.slice(12).filter(pick => pick !== null);
      
      // Create the draft object from current state
      const draftData = {
        id: draftId || currentDraftId,
        title: draftInfo.title || `${teamName || 'Untitled'} - ${tournament || 'Draft'}`,
        tournament: draftInfo.tournament || tournament,
        teamName: teamName,
        date: draftInfo.date || draftDate,
        radiantHeroes: radiantHeroes,
        direHeroes: direHeroes
      };

      // Save to file system
      const savedId = await saveDraft(draftData);

      // Update state if needed
      if (savedId !== draftId) {
        navigate(`/draft/${savedId}`, { replace: true });
      }

      // Show success message
      showNotification('Draft saved successfully!', 'success');
    } catch (err) {
      console.error('Error saving draft:', err);
      setError('Failed to save draft: ' + err.message);
      showNotification('Failed to save draft: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Effects to load draft if draftId is provided
  useEffect(() => {
    const loadDraftData = async () => {
      if (draftId) {
        try {
          setLoading(true);
          const draft = await loadDraft(draftId);
          if (draft) {
            // Set up draft info 
            setDraftInfo({
              title: draft.title || '',
              tournament: draft.tournament || '',
              date: draft.date || '',
              isPublic: draft.isPublic || false
            });

            // Set up picks from draft data using DraftContext
            let newPicks = Array(24).fill(null);
            if (draft.radiantHeroes && Array.isArray(draft.radiantHeroes)) {
              draft.radiantHeroes.forEach((heroData, index) => {
                if (heroData && index < 12) {
                  newPicks[index] = heroData;
                }
              });
            }
            
            if (draft.direHeroes && Array.isArray(draft.direHeroes)) {
              draft.direHeroes.forEach((heroData, index) => {
                if (heroData && index < 12) {
                  newPicks[index + 12] = heroData;
                }
              });
            }
            
            // Update the first scenario with the loaded picks
            if (scenariosFirstPick.length > 0) {
              updateScenario('first', scenariosFirstPick[0].id, { picks: newPicks });
            }
          }
        } catch (err) {
          console.error('Error loading draft:', err);
          setError('Failed to load draft: ' + err.message);
          showNotification('Failed to load draft: ' + err.message, 'error');
        } finally {
          setLoading(false);
        }
        }
      };
      
    loadDraftData();
  }, [draftId, loadDraft, showNotification]);

  const handleDraftInfoChange = (e) => {
    const { name, value } = e.target;
    setDraftInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBoxClick = (position) => {
    setSelectedBox(position);
  };
  
  const handleHeroSelect = (hero) => {
    if (selectedBox !== -1 && hero && currentScenario) {
      const newPicks = [...(currentScenario.picks || Array(24).fill(null))];
      newPicks[selectedBox - 1] = {
          hero: hero,
          isBanned: false,
          position: selectedBox
      };
      updateScenario('first', currentScenario.id, { picks: newPicks });
    }
  };
  
  const toggleBan = () => {
    if (selectedBox !== -1 && currentScenario?.picks?.[selectedBox - 1]) {
      const newPicks = [...currentScenario.picks];
      newPicks[selectedBox - 1] = {
        ...newPicks[selectedBox - 1],
        isBanned: !newPicks[selectedBox - 1].isBanned
      };
      updateScenario('first', currentScenario.id, { picks: newPicks });
    }
  };
  
  const handleClearAll = () => {
    if (currentScenario) {
      updateScenario('first', currentScenario.id, { picks: Array(24).fill(null) });
      setSelectedBox(-1);
      showNotification('All selections cleared', 'info');
    }
  };

  // Handle match data being pulled and loaded
  const handleMatchDataPulled = (matchData) => {
    try {
      // Update the first scenario with the pulled picks data
      if (scenariosFirstPick.length > 0) {
        updateScenario('first', scenariosFirstPick[0].id, { 
          picks: matchData.picks 
        });
      }
      
      // Update the draft info fields
      setDraftInfo({
        title: matchData.title,
        tournament: matchData.tournament,
        date: matchData.date,
        isPublic: false
      });

      // Also update the context fields for consistency
      setTeamName(matchData.teamName);
      setTournament(matchData.tournament);
      setDraftDate(matchData.date);

      // Clear any selected box
      setSelectedBox(-1);
      
      showNotification(`Loaded match data: ${matchData.title}`, 'success');
    } catch (error) {
      console.error('Error handling pulled match data:', error);
      showNotification('Error loading match data', 'error');
    }
  };

  return (
    <>
      <StyledAppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Draft Simulator {currentDraftId ? `(Editing Draft)` : '(New Draft)'}
          </Typography>
          
          {/* Draft controls */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained" 
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveClick}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save to File'}
            </Button>
          </Box>
        </Toolbar>
      </StyledAppBar>
      
      {/* Main content */}
      <Box
        sx={{
          padding: 3,
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        {/* Match Data Pull Component */}
        <MatchDataPull onDataPulled={handleMatchDataPulled} />

        {/* Title and controls */}
        <Grid container spacing={2} mb={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Draft Title"
              variant="outlined"
              name="title"
              value={draftInfo.title}
              onChange={handleDraftInfoChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Tournament"
              variant="outlined"
              name="tournament"
              value={draftInfo.tournament}
              onChange={handleDraftInfoChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Date"
              variant="outlined"
              type="date"
              name="date"
              value={draftInfo.date}
              onChange={handleDraftInfoChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={handleClearAll}
            >
              Clear All Selections
            </Button>
          </Grid>
        </Grid>
        
        {/* Hero selector */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={9}>
            <Autocomplete
              options={allHeroes}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label="Select Hero" />}
              onChange={(event, newValue) => handleHeroSelect(newValue)}
              disabled={selectedBox === -1}
              value={currentHero}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={toggleBan}
              disabled={selectedBox === -1 || !currentScenario?.picks?.[selectedBox - 1]}
            >
              {selectedBox !== -1 && currentScenario?.picks?.[selectedBox - 1]?.isBanned
                ? 'Mark as Pick' 
                : 'Mark as Ban'}
            </Button>
          </Grid>
        </Grid>
        
        {/* Draft board */}
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <DraftBoard />
        </Box>
      </Box>
      
      {/* Error/success notifications */}
      <Snackbar 
        open={Boolean(successMessage)} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DraftSimulator; 
